import type { FacilitySubmodule, PortalRole, UserProfile } from '../types/auth';

export type ManproPrototypeRole = {
  userType: string;
  portalRole: PortalRole;
  roleName: string;
  label: string;
  description: string;
  dashboardFocus: string;
  permissions: string[];
  facilitySubmodules: FacilitySubmodule[];
  flow: string[];
};

export type ManproPrototypeTaskStatus =
  | 'intake'
  | 'proposal'
  | 'planning'
  | 'execution'
  | 'validation'
  | 'reporting';

export type ManproPrototypeTask = {
  id: string;
  title: string;
  owner: string;
  assignedRole: string;
  status: ManproPrototypeTaskStatus;
  note: string;
};

export const manproPrototypeRoles: ManproPrototypeRole[] = [
  {
    userType: 'super-admin',
    portalRole: 'provider',
    roleName: 'super_admin',
    label: 'Super Admin',
    description: 'Mengatur seluruh tenant, provider, akses role, dan monitoring lintas proyek.',
    dashboardFocus: 'Governance dan kontrol lintas provider.',
    permissions: [
      'project_hub',
      'cleaning',
      'ac',
      'waste',
      'procurement',
      'laundry',
      'role_management',
    ],
    facilitySubmodules: [
      { key: 'fm_cleaning', value: 'Cleaning' },
      { key: 'fm_ac', value: 'AC Monitoring' },
      { key: 'fm_waste', value: 'Waste Management' },
      { key: 'fm_procurement', value: 'Procurement' },
      { key: 'fm_laundry', value: 'Laundry' },
    ],
    flow: [
      'Review proyek masuk dari owner/customer.',
      'Assign provider atau cek provider yang sudah menerima pekerjaan.',
      'Pantau planning, eksekusi, validasi, dan reporting lintas provider.',
      'Audit SLA, issue, dan dokumen serah terima.',
    ],
  },
  {
    userType: 'admin-employee',
    portalRole: 'provider',
    roleName: 'admin_employee',
    label: 'Admin Employee',
    description: 'Operator internal yang membantu validasi data, follow-up proyek, dan reporting.',
    dashboardFocus: 'Operasional harian dan tindak lanjut administrasi proyek.',
    permissions: ['project_hub', 'cleaning', 'ac', 'procurement'],
    facilitySubmodules: [
      { key: 'fm_cleaning', value: 'Cleaning' },
      { key: 'fm_ac', value: 'AC Monitoring' },
      { key: 'fm_procurement', value: 'Procurement' },
    ],
    flow: [
      'Verifikasi kelengkapan kebutuhan owner.',
      'Menyiapkan catatan administrasi sebelum provider mulai.',
      'Memonitor update progress dan bukti kerja.',
      'Menyiapkan ringkasan laporan untuk admin utama.',
    ],
  },
  {
    userType: 'provider-merchant',
    portalRole: 'provider',
    roleName: 'provider_merchant',
    label: 'Provider Merchant',
    description: 'PIC provider yang menerima peluang, mengirim penawaran, dan mengelola delivery.',
    dashboardFocus: 'Pipeline proyek, penawaran, assignment tim, dan revenue delivery.',
    permissions: ['project_hub', 'cleaning', 'ac', 'waste', 'procurement', 'laundry'],
    facilitySubmodules: [
      { key: 'fm_cleaning', value: 'Cleaning' },
      { key: 'fm_ac', value: 'AC Monitoring' },
      { key: 'fm_waste', value: 'Waste Management' },
      { key: 'fm_procurement', value: 'Procurement' },
      { key: 'fm_laundry', value: 'Laundry' },
    ],
    flow: [
      'Melihat peluang proyek dari owner.',
      'Mengirim estimasi biaya, durasi, dan catatan penawaran.',
      'Membuat baseline planning setelah dipilih owner.',
      'Assign tukang/serviceman ke task; pengerjaan dan evidence dikirim dari mobile app.',
    ],
  },
  {
    userType: 'provider-architect',
    portalRole: 'provider',
    roleName: 'provider_architect',
    label: 'Provider Architect',
    description: 'Role perencana teknis untuk scope, desain solusi, baseline, dan validasi metode.',
    dashboardFocus: 'Scope teknis, dokumen metode kerja, dan baseline rencana.',
    permissions: ['project_hub', 'planning', 'validation'],
    facilitySubmodules: [
      { key: 'fm_cleaning', value: 'Cleaning' },
      { key: 'fm_ac', value: 'AC Monitoring' },
    ],
    flow: [
      'Membaca brief, lokasi, dan dokumen pendukung dari owner.',
      'Menyusun scope teknis dan milestone baseline.',
      'Memberikan catatan teknis untuk provider merchant.',
      'Memvalidasi evidence dari mobile dan mengirim revisi kembali ke mobile jika perlu.',
    ],
  },
  {
    userType: 'provider-serviceman',
    portalRole: 'provider',
    roleName: 'provider_serviceman',
    label: 'Provider Serviceman / Tukang Mobile',
    description: 'Pelaksana lapangan yang menerima task, update progress, dan unggah evidence melalui aplikasi mobile.',
    dashboardFocus: 'User lapangan berbasis mobile; web hanya menampilkan hasil sinkronisasi progress dan evidence.',
    permissions: ['project_hub', 'execution'],
    facilitySubmodules: [
      { key: 'fm_cleaning', value: 'Cleaning' },
      { key: 'fm_ac', value: 'AC Monitoring' },
    ],
    flow: [
      'Menerima task dari assignment web melalui aplikasi mobile.',
      'Update progress, catatan lapangan, dan evidence dari mobile app.',
      'Web internal menerima sinkronisasi progress/evidence sebagai monitoring.',
      'Jika validasi ditolak, revisi dikirim kembali ke mobile untuk dikerjakan ulang.',
    ],
  },
];

export const defaultManproPrototypeTasks: ManproPrototypeTask[] = [
  {
    id: 'proto-task-1',
    title: 'Renovasi Lobby Kantor Pusat',
    owner: 'PT Aditya Properti',
    assignedRole: 'provider_merchant',
    status: 'proposal',
    note: 'Menunggu finalisasi penawaran provider.',
  },
  {
    id: 'proto-task-2',
    title: 'Baseline AC Maintenance Hotel',
    owner: 'Hotel Bumi Raya',
    assignedRole: 'provider_architect',
    status: 'planning',
    note: 'Scope teknis dan milestone sedang disusun.',
  },
  {
    id: 'proto-task-3',
    title: 'Cleaning Deep Service Area Produksi',
    owner: 'PT Sentra Manufaktur',
    assignedRole: 'provider_serviceman',
    status: 'execution',
    note: 'Progress dan evidence dikirim oleh tukang melalui aplikasi mobile.',
  },
];

export const getPrototypeRoleByUserType = (userType: string) =>
  manproPrototypeRoles.find((role) => role.userType === userType);

export const createPrototypeUserProfile = (
  prototypeRole: ManproPrototypeRole,
): UserProfile => ({
  name: prototypeRole.label,
  email: `${prototypeRole.userType}@prototype.manpro`,
  first_name: prototypeRole.label,
  user_type: prototypeRole.userType,
  roles: [{ role_name: prototypeRole.roleName }],
  facility_submodules: prototypeRole.facilitySubmodules,
});
